import { Image } from '@/components/ui/image';
import cn from 'classnames';
import usePrice, { calcCurrency } from '@/lib/use-price';
import { useModalAction } from '@/components/ui/modal/modal.context';
import { productPlaceholder } from '@/lib/placeholders';
import { useRouter } from 'next/router';
import Link from 'next/link';
import { twMerge } from 'tailwind-merge';
import dynamic from 'next/dynamic';
import MissingProduct from '../widgets/product-out-of-stock/card-out-of-stock';
import { ISearchProduct } from '@/types';
import { useMemo, memo } from 'react';
import { showCompareEcomprove } from '@/lib/utils/prices-comparison-check/comparison-check';
import { AddPromotionalPackToCartButton } from '../add-to-cart/add-promotional-pack-to-cart';
import { CashbackInfo } from '../widgets/cashback-info/cashback-info';
import { generateInstallmentOptions, resolveProductMaxInstallments } from '@/lib/utils/creditCard/creditCard';
import {
  getProductMaxInstallmentsFromCharacteristics,
  getPBMSalePrice,
  hasEnoughStockForPromotionalPack,
  promotionalPackHasPayRule,
  shouldShowPBM,
} from '@/lib/utils/product';
import { creditCardConfig } from '@/lib/utils/creditCard/constants';
import { useSettings } from '@/framework/settings';
import { Routes } from '@/config/routes';
import { isMobile } from 'react-device-detect';
import TagGenericoIcon from '@/components/icons/tag-generico-icon';
import ExclamationMarkIcon from '@/components/icons/exclamation-mark-icon';
import { CHARACTERISTICS_MAP } from '@/lib/constants/characteristics';
import { MAIN_SEARCH_INDEX_ID, TYPESENSE_PUBLISHED_PRODUCTS_FILTER } from '@/lib/services/typesense/constants';
import { MIN_DISCOUNT_PERCENTAGE } from '@/framework/utils/constants';
import { shouldOptimizeNextImage } from '@/lib/optimizable-image-url';
import { trackHomeItemSelect, type HomeListContext } from '@/lib/gtm';


const AddToCart = dynamic(
  () =>
    import('@/components/products/add-to-cart/add-to-cart').then(
      (module) => module.AddToCart,
    ),
  { ssr: false },
);

type ProductLinkProps = {
  href: string | null;
  className?: string;
  onClick: (e: React.MouseEvent) => void;
  children: React.ReactNode;
};

/** Products without a slug have no page to link to, so render a plain wrapper instead of `href="#"`. */
function ProductLink({ href, className, onClick, children }: ProductLinkProps) {
  if (!href) return <div className={className}>{children}</div>;
  return (
    <Link href={href} prefetch={false} onClick={onClick} className={className}>
      {children}
    </Link>
  );
}

type HeliumProps = {
  product: ISearchProduct;
  className?: string;
  listContext?: HomeListContext;
  imagePriority?: boolean;
};

function Helium({
  product,
  className,
  listContext,
  imagePriority = false,
}: Readonly<HeliumProps>) {
  const router = useRouter();
  const { query } = router;
  const { name, image, quantity } = product ?? {};
  const { settings } = useSettings();
  const productHref = product?.slug ? Routes.product(product.slug, product?.sku) : null;

  const dosage = useMemo(
    () => product?.characteristics?.find((item) => item?.id === 6)?.value || '',
    [product],
  );

  const referenceProduct = useMemo(
    () => product?.characteristics?.find((item) => item?.id === CHARACTERISTICS_MAP.REFERENCE.id)?.value || null,
    [product],
  );

  const hasGeneric = useMemo(() => {
    const hasGenericChar = product?.characteristics?.find((item) => item?.id === CHARACTERISTICS_MAP.HAS_GENERIC.id);

    if (!hasGenericChar) return false;

    // Parse the value - it may come as "true" or "false" as string
    const value = hasGenericChar.value;
    if (typeof value === 'string') {
      return value.toLowerCase() === 'true';
    }
    return Boolean(value);
  }, [product]);

  const handleGenericClick = (e: React.MouseEvent) => {
    // Prevent both link navigation and parent click (opening product modal).
    e.preventDefault();
    e.stopPropagation();

    // Navigate to search page with the filter for associated products
    const filterString = `${TYPESENSE_PUBLISHED_PRODUCTS_FILTER} && $associated_products(parent_sku:=${product.sku} && association_type:='generico')`;
    router.push({
      pathname: Routes.search,
      query: {
        [`${MAIN_SEARCH_INDEX_ID}[configure][filters]`]: filterString,
      },
    });
  };

  // const baseAmount = product.price

  const { price, basePrice, discount, discountValue } = usePrice({
    amount: product.sale_price,
    baseAmount: product.price,
  });

  // Calculate maximum installment option for the product
  const maxInstallment = useMemo(() => {
    const installmentRules =
      settings?.installmentRules ||
      creditCardConfig.creditCard.installmentRules;
    const productMaxInstallments =
      getProductMaxInstallmentsFromCharacteristics(product.characteristics);
    const maxInstallmentsCap = resolveProductMaxInstallments(
      product.sale_price,
      installmentRules,
      productMaxInstallments,
    );
    const options = generateInstallmentOptions(
      product.sale_price,
      installmentRules,
      maxInstallmentsCap,
    );

    return options[options.length - 1];
  }, [product.sale_price, product.characteristics, settings?.installmentRules]);

  const isOutOfStock = Number(quantity) <= 0;

  const { openModal } = useModalAction();

  function handleProductQuickView({ selectedTab }: { selectedTab?: string }) {

    if (isMobile) {
      router.push(Routes.product(product.slug, product.sku));
      return;
    }

    return openModal('PRODUCT_DETAILS', {
      productSlug: product.slug,
      product: product,
      selectedTab,
    });
  }

  const handleProductLinkClick = (e: React.MouseEvent, opts?: { selectedTab?: string }) => {
    if (listContext?.sectionId) {
      trackHomeItemSelect(product, listContext);
    }
    // On desktop we open the quick view modal instead of navigating.
    if (!isMobile) {
      e.preventDefault();
      handleProductQuickView({ selectedTab: opts?.selectedTab });
    }
  };

  return (
    <article
      className={twMerge(
        cn(
          'product-card cart-type-helium h-full overflow-hidden rounded-[24px] bg-light transition-shadow duration-200 shadow-[3px_3px_10px_rgba(0,0,0,0.10)]',
          className,
        ),
      )}
    >
      <ProductLink
        href={productHref}
        onClick={(e) => handleProductLinkClick(e)}
        className={cn(
          'relative flex h-44 w-auto items-center justify-center sm:h-52',
          query?.pages
            ? query?.pages?.includes('medicine')
              ? 'm-4 mb-0'
              : ''
            : '',
        )}
      >
        <span className="sr-only">{"Imagem do Produto"}</span>
        <Image
          src={image?.original ?? productPlaceholder}
          alt={name}
          fill
          sizes="(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 280px"
          priority={imagePriority}
          unoptimized={!shouldOptimizeNextImage(image?.original ?? productPlaceholder)}
          className="block object-contain product-image cursor-pointer mt-[18px]"
        />
        {hasGeneric ? (
          <span
            role="button"
            tabIndex={0}
            onClick={handleGenericClick}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleGenericClick(e as unknown as React.MouseEvent);
              }
            }}
            className="absolute top-4 left-4 md:left-2 cursor-pointer z-10 flex items-center gap-2"
          >
            <TagGenericoIcon className="w-9 h-9 sm:w-12 sm:h-12 md:w-8 md:h-8" />
            <div className="text-left">
              <p className="text-[12px] sm:text-[11px] font-bold text-gray-700 leading-tight">Genéricos</p>
              <div className="flex items-center">
                <p className="text-[12px] sm:text-[11px] font-semibold text-[#F0567A] leading-tight">Compre aqui!</p>
                <ExclamationMarkIcon className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
              </div>
            </div>
          </span>
        ) : referenceProduct ? (
          <div className="absolute top-4 flex items-center left-4 md:left-2 gap-2">
            <TagGenericoIcon className="w-9 h-9 sm:w-12 sm:h-12 md:w-8 md:h-8" />
            <div className="text-left">
              <p className="text-[12px] sm:text-[11px] font-bold text-black leading-tight">Genérico do:</p>
              <p className="text-[12px] sm:text-[11px] font-bold text-blue-600 leading-tight">{referenceProduct}</p>
            </div>
          </div>
        ) : null}
        {Number(discountValue) >= MIN_DISCOUNT_PERCENTAGE && !isOutOfStock && discount && (
          <div className="absolute top-3 rounded-full bg-yellow-500 px-1.5 text-xs font-semibold leading-6 text-black ltr:right-3 rtl:left-3 sm:px-2 md:top-4 md:px-2.5 ltr:md:right-4 rtl:md:left-4">
            {discount}
          </div>
        )}

        {isOutOfStock && (
          <div className="absolute top-3 rounded-full bg-[#FEE2E8] px-1.5 text-xs font-semibold leading-6 text-red-500 ltr:right-3 rtl:left-3 sm:px-2 md:top-4 md:px-2.5 ltr:md:right-4 rtl:md:left-4">
            Indisponível
          </div>
        )}

        {product.cashback_price && (
          <div className="absolute bottom-0">
            <CashbackInfo value={product.cashback_price} />
          </div>
        )}
      </ProductLink>
      {/* End of product image */}

      <header className="relative flex min-w-0 flex-col h-52 justify-between p-5 leading-3 gap-2">
        <div className="min-w-0">
          <h3 className="mb-2 min-w-0">
            <ProductLink
              href={productHref}
              onClick={(e) => handleProductLinkClick(e)}
              className="line-clamp-2 w-full min-w-0 break-words text-base text-[0.85rem] font-medium leading-snug text-accent"
            >
              {name}
            </ProductLink>
          </h3>
          <p className="text-xs text-[0.60rem]  text-gray-500">
            {product.brand} {dosage} {product.manufacturer}
          </p>
        </div>
        {/* End of product info */}

        {!isOutOfStock && (
          <div className="relative flex flex-col gap-2">
            {showCompareEcomprove({
              product_prices: product?.product_prices,
              current_product_price: product?.price,
              current_product_sale_price: product?.sale_price,
            }) && (
                <button type="button"
                  className={`py-1 px-2 text-[11px] font-medium rounded-full focus:outline-none bg-[rgba(240,86,122,0.1)] text-red-700 transition duration-300 ease-in-out whitespace-nowrap`}
                  onClick={() =>
                    handleProductQuickView({ selectedTab: 'compare-e-comprove' })
                  }
                >
                  Compare e Comprove
                </button>
              )}
            {/* PBM Program pill */}
            {shouldShowPBM({
              characteristics: product?.characteristics,
              productPrices: product?.product_prices,
            }) && (
                <button type="button"
                  className={`overflow-hidden text-[11px] font-bold rounded-full focus:outline-none ${promotionalPackHasPayRule(product?.characteristics)
                    ? 'bg-amber-100'
                    : 'bg-[#e1ebf4]'} text-blue-800 transition duration-300 ease-in-out whitespace-nowrap`}
                  onClick={() => handleProductQuickView({ selectedTab: 'pbm' })}
                >
                  <div className="grid grid-cols-[min-content,1fr]">
                    <span
                      className={`mr-2 py-1 px-2 ${promotionalPackHasPayRule(product?.characteristics)
                        ? 'bg-amber-300'
                        : 'bg-[#197fdb] text-white'} flex items-center justify-center`}
                    >
                      {calcCurrency({
                        amount: Number(getPBMSalePrice(product?.product_prices) || 0),
                        settings: {
                          currency: settings?.currency,
                          currencyOptions: settings?.currencyOptions,
                        },
                      }).price}
                    </span>
                    <span className="mr-2 py-1 px-2 flex items-center justify-center">
                      Ativar benefício Lab.
                    </span>
                  </div>
                </button>
              )}
            {hasEnoughStockForPromotionalPack(product) && (
              <AddPromotionalPackToCartButton product={product} />
            )}
          </div>
        )}

        <div className="relative flex items-center justify-between min-h-6">
          {!isOutOfStock && (
            <>
              <div className="relative">
                {!!product?.price &&
                  !!basePrice &&
                  Number(discountValue) >= MIN_DISCOUNT_PERCENTAGE && (
                    <del className="absolute text-xs italic whitespace-nowrap text-opacity-75 -top-4 text-muted md:-top-3">
                      De: {basePrice}
                    </del>
                  )}
                <span className="text-base font-semibold whitespace-nowrap text-dark-blue md:text-sm">
                  Por: {price}
                </span>
                {maxInstallment && (
                  <span className="block text-accent text-[12px] leading-3">
                    ou {maxInstallment.label.replace(/ sem juros$/, '')}{' '}
                    <span className="whitespace-nowrap mt-[1px]">sem juros</span>
                  </span>
                )}
              </div>

              <AddToCart
                data={{ ...product }}
                variant="single"
                disabled={false}
                attributionContext={listContext}
              />
            </>
          )}

          {isOutOfStock && <MissingProduct productId={product.product_id} />}
          {/* End of product price */}
        </div>
      </header>
    </article>
  );
}

export default memo(Helium);
